import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError, getViewAsVendor } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, DollarSign, BookOpen, Car as CarIcon, FileText as FileTextIcon, Wand2,
         ChevronDown, ChevronUp, Menu as MenuIcon, CalendarRange } from "lucide-react";
import TodayBookingFilters, { applyTodayFilter } from "@/components/bookings/TodayBookingFilters";
import PendingApprovalsPanel from "@/components/bookings/PendingApprovalsPanel";
import { toast } from "sonner";
import VendorTabsList from "@/components/vendor/VendorTabsList";
import VendorRatesTab from "@/components/vendor/VendorRatesTab";
import VendorRevenueChart from "@/components/VendorRevenueChart";
import CarForm from "@/components/vendor/CarForm";
import VendorCarsList from "@/components/vendor/VendorCarsList";
import VendorBulkEditDialog from "@/components/vendor/VendorBulkEditDialog";
import VendorScheduledJobsTile from "@/components/vendor/VendorScheduledJobsTile";
import VendorBookingsList from "@/components/vendor/VendorBookingsList";
import VendorRiskSignalsTile from "@/components/vendor/VendorRiskSignalsTile";
import VendorChatHandoffsPanel from "@/components/vendor/VendorChatHandoffsPanel";
import WithholdDepositDialog from "@/components/vendor/WithholdDepositDialog";
import VendorPayouts from "@/components/vendor/VendorPayouts";
import VendorPlatformInvoicesCard from "@/components/vendor/VendorPlatformInvoicesCard";
import VendorInvoicesTab from "@/components/vendor/VendorInvoicesTab";
import VendorInvoiceBrandingPanel from "@/components/vendor/VendorInvoiceBrandingPanel";
import FleetFinanceTab from "@/components/vendor/FleetFinanceTab";
import FleetHealthTile from "@/components/vendor/FleetHealthTile";
import VendorStaffTab from "@/components/vendor/VendorStaffTab";
import VendorDocumentsTab from "@/components/vendor/VendorDocumentsTab";
import VendorActivityLog from "@/components/vendor/VendorActivityLog";
import VendorAlertsSummary from "@/components/vendor/VendorAlertsSummary";
import VendorBusinessProfile from "@/components/vendor/VendorBusinessProfile";
import VendorReferralsPanel from "@/components/vendor/VendorReferralsPanel";
import VendorEasyPosFiscalizationPanel from "@/components/vendor/VendorEasyPosFiscalizationPanel";
import VendorBookingWidgetPanel from "@/components/vendor/VendorBookingWidgetPanel";
import VendorApiKeysPanel from "@/components/vendor/VendorApiKeysPanel";
import VendorGoogleReviewsCard from "@/components/vendor/VendorGoogleReviewsCard";
import VendorPoliciesEditor from "@/components/vendor/VendorPoliciesEditor";
import VendorNoShowPanel from "@/components/vendor/VendorNoShowPanel";
import VehicleReportsTab from "@/components/vendor/VehicleReportsTab";

// Iter 69 — Labels for the collapsed header so the vendor always sees
// which section is open even when the menu is closed.
const _VENDOR_TAB_LABELS = {
  cars: "My cars",
  calendar: "Calendar",
  bookings: "Bookings",
  payouts: "Payouts",
  billing: "Billing",
  "fleet-finance": "Fleet finance",
  reports: "Reports",
  fiscalization: "Fiscalization",
  widget: "Booking widget",
  rates: "Rates",
  staff: "Staff",
  activity: "Activity log",
  documents: "Documents",
  profile: "Business profile",
};
import StripeConnectCard from "@/components/vendor/StripeConnectCard";
import VendorQuotesPanel from "@/components/vendor/VendorQuotesPanel";
import VendorAccessInboxCard from "@/components/vendor/VendorAccessInboxCard";
import VendorBillingCard from "@/components/vendor/VendorBillingCard";
import VendorPaymentsCard from "@/components/vendor/VendorPaymentsCard";
import VendorStripeAutoPayCard from "@/components/vendor/VendorStripeAutoPayCard";
import VendorPaymentMethodsCard from "@/components/vendor/VendorPaymentMethodsCard";
import BookingChangeRequestsInbox from "@/components/vendor/BookingChangeRequestsInbox";
import ChangePasswordCard from "@/components/profile/ChangePasswordCard";
import VendorTwoFactorPanel from "@/components/vendor/VendorTwoFactorPanel";
import ManualBookingDialog from "@/components/booking/ManualBookingDialog";
import VendorDiscountTiers from "@/components/vendor/VendorDiscountTiers";
import VendorConversionWidgetsPanel from "@/components/vendor/VendorConversionWidgetsPanel";
import BookingsCalendar from "@/components/calendar/BookingsCalendar";
import Money from "@/components/Money";
import OpsSummaryCards from "@/components/ops/OpsSummaryCards";
import OpsBottomNav from "@/components/ops/OpsBottomNav";
import QuickActionsFab from "@/components/ops/QuickActionsFab";
import MobileBookingSearch from "@/components/ops/MobileBookingSearch";
import { CalendarPlus, CreditCard, CalendarOff, Zap } from "lucide-react";

export default function VendorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "cars";
  const [activeTab, setActiveTab] = useState(initialTab);
  // Iter 69 — Top, collapsible menu. Default collapsed so the dashboard
  // opens with maximum vertical room for content. Persists across reloads.
  const [tabsCollapsed, setTabsCollapsed] = useState(() => {
    try { return localStorage.getItem("rentalcar_vendor_tabs_collapsed") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("rentalcar_vendor_tabs_collapsed", tabsCollapsed ? "1" : "0"); }
    catch { /* private mode */ }
  }, [tabsCollapsed]);
  const [cars, setCars] = useState([]);
  const [bookings, setBookings] = useState([]);
  // Iter 76 — quick day filter (today / pickups / returns / overdue), Albania time.
  const [todayFilter, setTodayFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [withholdTarget, setWithholdTarget] = useState(null);
  const [depositLoadingId, setDepositLoadingId] = useState(null);
  const [unreadByBooking, setUnreadByBooking] = useState({});
  const [pendingChangeCount, setPendingChangeCount] = useState(0);
  const [pendingAccessCount, setPendingAccessCount] = useState(0);

  // Iter 67bg — keep active tab in sync if the URL changes after mount (e.g.
  // user clicks a deep-link from FleetHealthTile while already on the page).
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Count of bookings (any status) starting in the next 7 days — drives the badge on the Calendar tab.
  const upcomingCount = bookings.filter((b) => {
    if (!b.start_date) return false;
    const start = new Date(b.start_date);
    const now = new Date();
    const horizon = new Date();
    horizon.setDate(now.getDate() + 7);
    return start >= now && start <= horizon && b.status !== "cancelled";
  }).length;

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/messages/unread-count");
      setUnreadByBooking(data.by_booking || {});
    } catch (e) {
      console.error("unread count load failed:", e);
    }
  };

  const loadPendingChanges = async () => {
    try {
      const { data } = await api.get("/booking-changes/inbox");
      setPendingChangeCount(Array.isArray(data) ? data.length : 0);
    } catch (e) {
      // Non-fatal: dashboard keeps working without the badge.
      console.error("pending change-requests load failed:", e);
    }
  };

  const loadPendingAccess = async () => {
    try {
      const { data } = await api.get("/vendor-access/inbox");
      const pending = (data || []).filter((g) => g.status === "pending");
      setPendingAccessCount(pending.length);
    } catch (e) {
      // Non-fatal — the inbox card itself will surface errors when opened.
      console.error("pending access-requests load failed:", e);
    }
  };

  const load = async () => {
    try {
      const [c, b, s] = await Promise.all([
        api.get("/cars/mine"),
        api.get("/bookings/mine"),
        api.get("/vendor/stats"),
      ]);
      setCars(c.data); setBookings(b.data); setStats(s.data);
    } catch (e) {
      console.error("vendor dashboard load failed:", e);
    }
    loadUnread();
    loadPendingChanges();
    loadPendingAccess();
  };
  useEffect(() => { load(); }, []);

  // Poll unread-count + pending change-requests + admin access requests every 30s so
  // dashboard badges refresh even when the inbox panels are closed.
  useEffect(() => {
    const id = setInterval(() => { loadUnread(); loadPendingChanges(); loadPendingAccess(); }, 30000);
    return () => clearInterval(id);
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this car?")) return;
    await api.delete(`/cars/${id}`);
    toast.success("Deleted");
    load();
  };

  const togglePaused = async (carItem, nextActive) => {
    setCars((prev) => prev.map((c) => (c.id === carItem.id ? { ...c, is_active: nextActive } : c)));
    try {
      await api.patch(`/cars/${carItem.id}`, { is_active: nextActive });
      toast.success(nextActive ? "Listing activated" : "Listing paused");
    } catch (e) {
      setCars((prev) => prev.map((c) => (c.id === carItem.id ? { ...c, is_active: !nextActive } : c)));
      toast.error(formatApiError(e));
    }
  };

  const releaseDeposit = async (b) => {
    if (!window.confirm(`Release the full €${Number(b.deposit).toFixed(2)} deposit back to the customer?`)) return;
    setDepositLoadingId(b.id);
    try {
      await api.post(`/bookings/${b.id}/deposit/release`);
      toast.success("Deposit refunded to customer");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDepositLoadingId(null);
    }
  };

  if (!user) return null;

  // Iter 96 — clickable KPI cards → filtered sections.
  const handleSummaryNav = (key) => {
    if (["today", "pickups", "returns", "overdue"].includes(key)) {
      setActiveTab("bookings"); setTodayFilter(key);
    } else if (["active", "pending", "awaiting_payment"].includes(key)) {
      setActiveTab("bookings"); setTodayFilter(null);
    } else if (key === "vehicles") {
      setActiveTab("cars");
    } else if (key === "revenue") {
      setActiveTab("payouts");
    }
  };

  const handleBottomNav = (key) => {
    if (key === "dashboard") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    if (key === "today-ops") { navigate("/dashboard/today"); return; }
    if (key === "agreements") { navigate("/dashboard/agreements"); return; }
    setActiveTab(key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fabActions = [
    { key: "today-ops", label: "Today Operations", icon: Zap, onClick: () => navigate("/dashboard/today") },
    { key: "new-reservation", label: "New Reservation", icon: CalendarPlus, onClick: () => handleBottomNav("bookings") },
    { key: "add-vehicle", label: "Add Vehicle", icon: Plus, onClick: () => {
      if (user.vendor_status === "approved" || getViewAsVendor()) setOpen(true);
      else toast.info("Your vendor account must be approved before adding vehicles.");
    } },
    { key: "block-vehicle", label: "Block Vehicle Dates", icon: CalendarOff, onClick: () => handleBottomNav("calendar") },
    { key: "record-payment", label: "Payments & Billing", icon: CreditCard, onClick: () => handleBottomNav("billing") },
  ];

  return (
    <div>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-4 gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">Vendor dashboard</h1>
            <div className="text-sm text-neutral-500 mt-1">Welcome back, {user.name}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
          <MobileBookingSearch bookings={bookings} />
          {/* Iter 67ag — when admin/agent is in view-as-vendor mode the auth
              user is the admin (no vendor_status), so the original
              `user.vendor_status === "approved"` gate hid this button.
              Impersonation grants are only issued by already-approved vendors,
              so treating impersonation as "approved" for UI purposes is safe. */}
          {(user.vendor_status === "approved" || getViewAsVendor()) && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="add-car-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add new car
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>List a new car</DialogTitle>
                  <DialogDescription>Fill in the details to publish this car on RentalCar.al. You can edit anything later.</DialogDescription>
                </DialogHeader>
                <CarForm onSaved={() => { setOpen(false); load(); }} />
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>

        {/* Iter 77c — rental-management quick actions live at page level,
            directly under "Add new car", so vendors see them the moment
            they log in. Picking a filter jumps to the Bookings tab. */}
        <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="vendor-bookings-toolbar">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setActiveTab("calendar")}
            className="rounded-full gap-1.5 text-xs font-semibold border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="vendor-bookings-calendar-btn"
          >
            <CalendarRange className="h-3.5 w-3.5" /> Calendar
          </Button>
          <TodayBookingFilters
            bookings={bookings}
            active={todayFilter}
            onChange={(key) => {
              setTodayFilter(key);
              if (key) setActiveTab("bookings");
            }}
          />
        </div>

        {user.vendor_status !== "approved" && !getViewAsVendor() && (
          <div className="rounded-2xl bg-yellow-50 border border-yellow-200 px-5 py-4 mb-8 text-sm text-yellow-900" data-testid="vendor-pending-banner">
            Your vendor account is <strong className="capitalize">{user.vendor_status}</strong>. You'll be able to list cars once an admin approves your account.
          </div>
        )}

        {user.vendor_status === "approved" && user.documents_approved === false && !getViewAsVendor() && (
          <button
            type="button"
            onClick={() => setActiveTab("documents")}
            className="block w-full text-left rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 mb-8 text-sm text-amber-900 hover:bg-amber-100 transition"
            data-testid="vendor-docs-banner"
          >
            <div className="font-semibold flex items-center gap-2">
              <FileTextIcon className="h-4 w-4" /> Finish your business onboarding
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              {user.documents_approval_note || "Upload your business documents (registration, VAT, license, insurance) and sign any pending contracts. Click here to open Documents."}
            </div>
          </button>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Iter 69 — Collapsible top menu, mirrors the admin pattern. The
              header bar is always visible; clicking it toggles the full pill
              list (or grid on mobile). Default collapsed so vendors see
              their stats & revenue chart first. */}
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setTabsCollapsed((c) => !c)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-emerald-50/70 via-teal-50/40 to-sky-50/60 border border-emerald-100 shadow-sm ring-1 ring-emerald-50 hover:shadow-md transition"
              data-testid="vendor-tabs-collapse-toggle"
              aria-expanded={!tabsCollapsed}
              aria-controls="vendor-main-tabs"
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <span className="h-8 w-8 rounded-xl bg-white text-emerald-600 flex items-center justify-center ring-1 ring-emerald-200 shadow-sm shrink-0">
                  <MenuIcon className="h-4 w-4" />
                </span>
                <span className="font-bold text-sm text-emerald-900 truncate">
                  {_VENDOR_TAB_LABELS[activeTab] || "Menu"}
                </span>
              </span>
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-emerald-700 font-bold shrink-0">
                {tabsCollapsed ? "Open" : "Close"}
                {tabsCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </span>
            </button>
          </div>
          {!tabsCollapsed && (
            <VendorTabsList
              upcomingCount={upcomingCount}
              pendingChangeCount={pendingChangeCount}
              pendingAccessCount={pendingAccessCount}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          )}

          {/* Stats & revenue chart stay above the tab content, INSIDE Tabs
              so they remain between TabsList and the first TabsContent. */}
          {/* Iter 96 — clickable daily KPI cards (server-side /ops/summary). */}
          <div className="mt-2">
            <OpsSummaryCards role="vendor" onNavigate={handleSummaryNav} />
          </div>
          <FleetHealthTile />

          <div className="mb-12 mt-6">
            <VendorRevenueChart />
          </div>

          <TabsContent value="cars" className="mt-6">
            <VendorAlertsSummary onJumpToCar={(id) => {
              const car = cars.find((c) => c.id === id);
              if (car) setEditing(car);
            }} />
            {(cars?.length || 0) > 1 && (
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3 rounded-2xl border border-amber-200 bg-amber-50/50 px-4 py-3" data-testid="vendor-bulk-edit-banner">
                <div className="text-xs text-amber-900">
                  <strong>{cars.length} cars in your fleet.</strong> Apply one change to many cars in a single click.
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-100"
                  onClick={() => setBulkEditOpen(true)}
                  data-testid="vendor-bulk-edit-open"
                >
                  <Wand2 className="h-3.5 w-3.5 mr-1" /> Bulk edit fleet
                </Button>
              </div>
            )}
            <VendorScheduledJobsTile onChanged={() => load()} />
            <VendorCarsList
              cars={cars}
              onEdit={setEditing}
              onDelete={remove}
              onTogglePaused={togglePaused}
            />
            <VendorBulkEditDialog
              open={bulkEditOpen}
              onOpenChange={setBulkEditOpen}
              cars={cars}
              onApplied={() => load()}
            />
          </TabsContent>
          <TabsContent value="calendar" className="mt-6">
            <BookingsCalendar scope="vendor" />
          </TabsContent>
          <TabsContent value="bookings" className="mt-6">
            {/* Iter 89 — reservations awaiting host approval, with countdown. */}
            <PendingApprovalsPanel bookings={bookings} scope="vendor" onChanged={load} />
            {/* Iter 67ep — risk signals tile sits at the top of the
                Bookings tab. It's an actionable list (renters who
                haven't opened their agreement + need a courtesy call),
                so it earns the prime slot. */}
            <VendorRiskSignalsTile />
            {/* Iter 67el — AI-chat handoff inbox with chime on new entries. */}
            <div className="mb-4"><VendorChatHandoffsPanel /></div>
            <BookingChangeRequestsInbox onChanged={() => { load(); loadPendingChanges(); }} />
            {user?.manual_booking_enabled && (
              <div className="mb-4 flex items-center justify-between flex-wrap gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3 px-4" data-testid="vendor-manual-booking-banner">
                <div className="text-xs text-emerald-800">
                  <strong>Manual bookings enabled.</strong> You can record off-platform reservations directly here.
                </div>
                <ManualBookingDialog restrictToVendorId={user?.id} onCreated={load} />
              </div>
            )}
            {/* Iter 115 — shared quotes with live status (sent / approved / expired). */}
            {user?.manual_booking_enabled && <VendorQuotesPanel />}
            <VendorBookingsList
              bookings={applyTodayFilter(bookings, todayFilter)}
              depositLoadingId={depositLoadingId}
              onRelease={releaseDeposit}
              onWithhold={setWithholdTarget}
              onChanged={load}
              unreadByBooking={unreadByBooking}
              onUnreadChange={loadUnread}
            />
          </TabsContent>
          <TabsContent value="payouts" className="mt-6">
            <VendorPayouts />
            <div className="mt-6">
              <VendorPlatformInvoicesCard />
            </div>
          </TabsContent>
          <TabsContent value="billing" className="mt-6" data-testid="vendor-content-billing">
            <div className="space-y-10">
              <VendorBillingCard />
              <VendorStripeAutoPayCard />
              <VendorPaymentsCard />
              <VendorPaymentMethodsCard />
              {/* Iter 67bd — Rental invoices live under Billing for one-stop money flows. */}
              <div className="pt-2 border-t border-border" data-testid="vendor-billing-invoices-section">
                <VendorInvoicesTab />
              </div>
              <VendorInvoiceBrandingPanel />
            </div>
          </TabsContent>
          <TabsContent value="fleet-finance" className="mt-6" data-testid="vendor-content-fleet-finance">
            <FleetFinanceTab />
          </TabsContent>
          <TabsContent value="reports" className="mt-6" data-testid="vendor-content-reports">
            <VehicleReportsTab cars={cars} />
          </TabsContent>
          <TabsContent value="fiscalization" className="mt-6" data-testid="vendor-content-fiscalization">
            <VendorEasyPosFiscalizationPanel />
          </TabsContent>
          <TabsContent value="widget" className="mt-6" data-testid="vendor-content-widget">
            <VendorBookingWidgetPanel />
            {/* Iter 67eg — REST API keys for channel managers / external CRMs. */}
            <div className="mt-6"><VendorApiKeysPanel /></div>
          </TabsContent>
          <TabsContent value="rates" className="mt-6" data-testid="vendor-content-rates">
            <VendorRatesTab />
          </TabsContent>
          <TabsContent value="staff" className="mt-6" data-testid="vendor-content-staff">
            <VendorStaffTab />
          </TabsContent>
          <TabsContent value="activity" className="mt-6" data-testid="vendor-content-activity">
            <VendorActivityLog
              endpoint="/vendor/audit-logs"
              title="Activity log"
              subtitle="Every booking, invoice, vehicle, price, payment and staff change — with the person and timestamp behind it."
            />
          </TabsContent>
          <TabsContent value="documents" className="mt-6" data-testid="vendor-content-documents">
            <VendorDocumentsTab />
          </TabsContent>
          <TabsContent value="profile" className="mt-6">
            <VendorBusinessProfile />
            {/* Iter 67al — dedicated Google Reviews card with status pill +
                live preview. The same input lives inside the broader business
                profile, but vendors kept missing it; this surface is more
                discoverable and gives instant feedback. */}
            <div className="mt-6"><VendorGoogleReviewsCard /></div>
            <div className="mt-6"><VendorAccessInboxCard onChanged={loadPendingAccess} /></div>
            <VendorPoliciesEditor />
            {/* Iter 68 — No-Show policy + stats live next to the cancellation policy
                in the Settings tab so vendors manage all "what happens if the renter
                doesn't show / cancels" rules in one place. */}
            <div className="mt-6"><VendorNoShowPanel /></div>
            <VendorDiscountTiers />
            {/* Iter 69 — per-vendor toggle for the 3 car-detail conversion
                widgets (StayLonger / BookEarlier / SameFleetSaveX). */}
            <div className="mt-6"><VendorConversionWidgetsPanel /></div>
            {/* Iter 67ej — referral attribution + reward ledger. Vendors
                share their code with peer rental companies; once the
                referee lists ≥3 cars, the referrer earns 1 month
                commission-free. */}
            <div className="mt-6"><VendorReferralsPanel /></div>
            <div className="mt-6">
              <StripeConnectCard />
            </div>
            <div className="mt-6">
              <ChangePasswordCard />
            </div>
            <div className="mt-6">
              <VendorTwoFactorPanel />
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle>Edit car</DialogTitle>
              <DialogDescription>Update price, photos, insurance plans, add-ons, or availability for this car.</DialogDescription>
            </DialogHeader>
            {editing && (
              <CarForm car={editing} onSaved={() => { setEditing(null); load(); }} />
            )}
          </DialogContent>
        </Dialog>

        <WithholdDepositDialog
          booking={withholdTarget}
          onClose={() => setWithholdTarget(null)}
          onSuccess={load}
        />
      </div>
      {/* Iter 96 — mobile bottom nav + floating quick actions. */}
      <QuickActionsFab actions={fabActions} />
      <OpsBottomNav role="vendor" active={activeTab} onNavigate={handleBottomNav} />
      <div className="h-16 md:hidden" />
      <Footer />
    </div>
  );
}
